// src/pages/factors/Print_2.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../core/api';
import './Print_2.css';

const API_BASE = api.defaults.baseURL || '';
const imgUrl = (p) => {
  if (!p) return null;
  return /^(https?:|blob:|data:)/.test(p) ? p : `${API_BASE}${p}`;
};

const FA = '۰۱۲۳۴۵۶۷۸۹';
const toFa = (v) =>
  v === undefined || v === null ? '' : String(v).replace(/\d/g, (d) => FA[+d]);
const money = (n) => toFa(Math.round(Number(n) || 0).toLocaleString('en-US'));

const ICONS = {
  pin: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  globe: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
  mail: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  phone: 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z',
  mobile: 'M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z',
};

/* @page سراسری است و Print.css هم @page افقی دارد؛
   پس فقط وقتی این صفحه باز است، بعد از بقیه‌ی استایل‌ها تزریقش می‌کنیم */
const PAGE_CSS = `
@page { size: A4 portrait; margin: 10mm 12mm 15mm 12mm; }
@media print {
  html, body { width: 210mm; height: 297mm; }
  body { background: none; padding: 0; display: block; }
}
html.p2-active body { height: auto; }
`;

const withSeps = (arr) =>
  arr.flatMap((el, i) =>
    i ? [<span key={`s${i}`} className="p2-sep">|</span>, el] : [el]
  );

function Pill({ icon, label, children }) {
  return (
    <span className="p2-pill">
      <svg viewBox="0 0 24 24"><path d={ICONS[icon]} /></svg>
      <strong>{label}</strong> {children}
    </span>
  );
}

export default function Print2() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [factor, setFactor] = useState(null);
  const [seller, setSeller] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');

  const [showStamp, setShowStamp] = useState(false);
  const [showSign, setShowSign] = useState(false);

  const pageRef = useRef(null);
  const tableRef = useRef(null);
  const sigRef = useRef(null);

  /* ============== @page مخصوص همین صفحه ============== */
  useEffect(() => {
    const el = document.createElement('style');
    el.setAttribute('data-p2-page', '');
    el.textContent = PAGE_CSS;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  useEffect(() => {
  if (factor) document.title = `فاکتور ${factor.serial} | پنل فاکتور`;
}, [factor]);

  /* ============== دریافت داده‌ها ============== */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [fRes, cRes] = await Promise.all([
          api.get(`/factors/${id}`),
          api.get('/company'),
        ]);
        if (cancelled) return;
        const f = fRes.data?.factor || fRes.data;
        if (!f || !f.id) {
          setNotFound(true);
          return;
        }
        setFactor(f);
        setSeller(cRes.data?.company || {});
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
        else setError('خطا در دریافت اطلاعات فاکتور');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /* ============== شماره‌گذاری و شکستن صفحه‌ها موقع چاپ ============== */
  useEffect(() => {
    if (!factor) return undefined;

    const MM = 96 / 25.4;
    const mm = (n) => n * MM;
    const PAGE = 270; // ارتفاع قابل استفاده‌ی هر صفحه (mm)
    const FOOT = 7; // ارتفاع جای شماره صفحه (mm)
    const undo = [];
    const root = document.documentElement;

    const cleanup = () => {
      while (undo.length) undo.pop()();
      root.classList.remove('p2-active');
    };

    const run = () => {
      cleanup();
      const page = pageRef.current;
      const table = tableRef.current;
      const sig = sigRef.current;
      if (!page || !table || !sig) return;
      const rows = Array.from(table.querySelectorAll('tbody tr'));
      if (!rows.length) return;

      root.classList.add('p2-active');

      const pageTop = page.getBoundingClientRect().top;
      const theadH = table.tHead.getBoundingClientRect().height;
      const rects = rows.map((r) => r.getBoundingClientRect());
      const afterH =
        page.getBoundingClientRect().bottom -
        rects[rects.length - 1].bottom +
        mm(FOOT);
      const limit = mm(PAGE) - 3;
      const cap = limit - mm(FOOT);

      // تقسیم ردیف‌ها بین صفحه‌ها
      const pages = [];
      let cur = [];
      let origin = 0;
      let head = 0;
      let prevBottom = 0;
      rows.forEach((r, i) => {
        const top = rects[i].top - pageTop;
        const bottom = rects[i].bottom - pageTop;
        if (cur.length && head + bottom - origin > cap) {
          pages.push({ rows: cur, origin, head, used: head + prevBottom - origin });
          cur = [];
          origin = top;
          head = theadH;
        }
        cur.push(r);
        prevBottom = bottom;
      });
      pages.push({ rows: cur, origin, head, used: head + prevBottom - origin });

      // اگر جمع‌ها و امضا در صفحه‌ی آخر جا نشدند، یک ردیف به صفحه‌ی بعد می‌رود
      const last = pages[pages.length - 1];
      if (last.used + afterH > limit && last.rows.length > 1) {
        const moved = last.rows.pop();
        const mi = rows.indexOf(moved);
        const pb =
          rects[rows.indexOf(last.rows[last.rows.length - 1])].bottom - pageTop;
        last.used = last.head + pb - last.origin;
        const mTop = rects[mi].top - pageTop;
        pages.push({
          rows: [moved],
          origin: mTop,
          head: theadH,
          used: theadH + rects[mi].bottom - pageTop - mTop,
        });
      }

      // یک صفحه‌ای: شماره‌ای لازم نیست
      if (pages.length < 2) {
        cleanup();
        return;
      }

      const total = pages.length;
      const cols = table.tHead.rows[0].cells.length;
      const fa = (n) => n.toLocaleString('fa-IR');
      const mk = (tag, cls) => {
        const e = document.createElement(tag);
        e.className = cls;
        return e;
      };

      pages.forEach((p, k) => {
        const text = `صفحه ${fa(k + 1)} از ${fa(total)}`;

        if (k > 0) {
          const first = p.rows[0];
          first.style.breakBefore = 'page';
          first.style.pageBreakBefore = 'always';
          undo.push(() => {
            first.style.breakBefore = '';
            first.style.pageBreakBefore = '';
          });
        }

        if (k < total - 1) {
          const sp = mk('tr', 'p2-pn-row');
          const fo = mk('tr', 'p2-pn-row');
          const c1 = document.createElement('td');
          const c2 = document.createElement('td');
          c1.colSpan = cols;
          c2.colSpan = cols;
          c1.style.height = `${Math.max(0, cap - p.used)}px`;
          c2.className = 'p2-pn-label';
          c2.textContent = text;
          sp.appendChild(c1);
          fo.appendChild(c2);
          const lastRow = p.rows[p.rows.length - 1];
          lastRow.parentNode.insertBefore(fo, lastRow.nextSibling);
          lastRow.parentNode.insertBefore(sp, fo);
          undo.push(() => {
            sp.remove();
            fo.remove();
          });
        } else {
          const wrap = mk('div', 'p2-pn-bottom');
          const fill = document.createElement('div');
          fill.style.height = `${Math.max(0, limit - p.used - afterH)}px`;
          const lab = mk('div', 'p2-pn-last');
          lab.textContent = text;
          const parent = sig.parentNode;
          parent.insertBefore(wrap, sig);
          wrap.appendChild(fill);
          wrap.appendChild(sig);
          wrap.appendChild(lab);
          undo.push(() => {
            parent.insertBefore(sig, wrap);
            wrap.remove();
          });
        }
      });
    };

    window.addEventListener('beforeprint', run);
    window.addEventListener('afterprint', cleanup);
    return () => {
      window.removeEventListener('beforeprint', run);
      window.removeEventListener('afterprint', cleanup);
      cleanup();
    };
  }, [factor]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else {
      window.close();
      setTimeout(() => navigate('/dashboard/factors'), 150);
    }
  };

  /* ============== حالت‌های لود / خطا ============== */
  if (loading || notFound || error) {
    return (
      <div className="p2-wrap">
        <div className="p2-status">
          {loading && 'در حال بارگذاری فاکتور...'}
          {notFound && 'فاکتور یافت نشد'}
          {error}
        </div>
      </div>
    );
  }

  /* ============== محاسبات ============== */
  const items = (factor.items || []).map((it) => {
    const gross = Number(it.qty) * Number(it.price);
    const disc = Number(it.discount) || 0;
    const tax = Number(it.tax) || 0;
    return { ...it, gross, disc, tax, total: gross - disc + tax };
  });
  const sum = items.reduce(
    (s, it) => ({
      gross: s.gross + it.gross,
      disc: s.disc + it.disc,
      tax: s.tax + it.tax,
      total: s.total + it.total,
    }),
    { gross: 0, disc: 0, tax: 0, total: 0 }
  );
  const hasDiscount = sum.disc > 0;
  const widths = hasDiscount
    ? [4, 27, 12, 6, 12, 12, 12, 15]
    : [4, 33, 14, 6, 14, 14, 15];

  const payType = factor.paymentType || 'نقدی';
  const prepaid = Number(factor.prepaid) || 0;
  const isPrepaid = payType === 'پیش پرداخت' && prepaid > 0;
  const remaining = Math.max(sum.total - prepaid, 0);

  const buyer = factor.buyer || {};
  const logoUrl = imgUrl(seller.logo);
  const stampUrl = imgUrl(seller.stamp);
  const signUrl = imgUrl(seller.signature);
  const sellerMediaShown = (showStamp && stampUrl) || (showSign && signUrl);

  const addressParts = [
    seller.province && `استان ${seller.province}`,
    seller.city,
    seller.address,
    seller.postal && `کد پستی: ${toFa(seller.postal)}`,
  ].filter(Boolean);

  const rowWebMail = withSeps(
    [
      seller.website && (
        <Pill key="w" icon="globe" label="وب‌سایت:">
          <bdi dir="ltr">{seller.website}</bdi>
        </Pill>
      ),
      seller.email && (
        <Pill key="e" icon="mail" label="رایانامه:">
          <bdi dir="ltr">{seller.email}</bdi>
        </Pill>
      ),
    ].filter(Boolean)
  );

  const rowPhones = withSeps(
    [
      seller.landline && (
        <Pill key="l" icon="phone" label="تلفن ثابت:">
          {toFa(seller.landline)}
        </Pill>
      ),
      seller.phone && (
        <Pill key="m" icon="mobile" label="تلفن همراه:">
          {toFa(seller.phone)}
        </Pill>
      ),
    ].filter(Boolean)
  );

  const buyerLocation = [buyer.city, buyer.province].filter(Boolean).join(' / ');
  const hasBuyerIds = buyer.national || buyer.eco || buyer.postal;
  const hasBank = seller.account_number || seller.bank_name || seller.card_number || seller.iban;

  return (
    <div className="p2-wrap">
      {/* ============ نوار دکمه‌ها (چاپ نمی‌شود) ============ */}
      <div className="p2-actions">
        <label
          className="p2-check"
          title={stampUrl ? '' : 'مهر در تنظیمات آپلود نشده است'}
        >
          <input
            type="checkbox"
            checked={showStamp}
            disabled={!stampUrl}
            onChange={(e) => setShowStamp(e.target.checked)}
          />
          <span>مهر</span>
        </label>
        <label
          className="p2-check"
          title={signUrl ? '' : 'امضا در تنظیمات آپلود نشده است'}
        >
          <input
            type="checkbox"
            checked={showSign}
            disabled={!signUrl}
            onChange={(e) => setShowSign(e.target.checked)}
          />
          <span>امضا</span>
        </label>
        <button type="button" className="p2-btn print" onClick={() => window.print()}>
          <i className="fa-solid fa-print" /> چاپ
        </button>
        <button type="button" className="p2-btn back" onClick={goBack}>
          <i className="fa-solid fa-arrow-right" /> بازگشت
        </button>
      </div>

      <div className="p2-page" ref={pageRef}>
        <div>
          {/* ============ هدر ============ */}
          <div className="p2-header">
            <div className="p2-header-top">
              <div className="p2-logo-box">
  {logoUrl && <img src={logoUrl} alt="لوگو" className="p2-logo" />}
</div>
              <div className="p2-title-box">
                <div className="p2-title-main">فاکتور فروش</div>
                <div className="p2-title-sub">{seller.name || ''}</div>
              </div>
              <div />
            </div>

            <div className="p2-seller-card">
              {addressParts.length > 0 && (
                <div className="p2-seller-row">
                  <Pill icon="pin" label="آدرس:">
                    {addressParts.join(' - ')}
                  </Pill>
                </div>
              )}
              {rowWebMail.length > 0 && <div className="p2-seller-row">{rowWebMail}</div>}
              {rowPhones.length > 0 && <div className="p2-seller-row">{rowPhones}</div>}
            </div>
          </div>

          {/* ============ اطلاعات صورتحساب ============ */}
          <div className="p2-meta-bar">
            <div className="p2-meta-item">
              <span className="p2-lbl">شماره فاکتور:</span>
              <span className="p2-val">{toFa(factor.serial)}</span>
            </div>
            <div className="p2-meta-item">
              <span className="p2-lbl">تاریخ صدور:</span>
              <span className="p2-val">{toFa(factor.date)}</span>
            </div>
            <div className="p2-meta-item">
              <span className="p2-lbl">شرایط پرداخت:</span>
              <span
                className="p2-val"
                style={{ color: payType === 'نقدی' ? '#16a34a' : '#d97706' }}
              >
                {payType}
              </span>
            </div>
          </div>

          {/* ============ مشخصات خریدار ============ */}
          <div className="p2-buyer-card">
            <div className="p2-buyer-head">مشخصات خریدار</div>
            <div className="p2-buyer-body">
              <div className="p2-buyer-row">
                <div className="p2-info-item">
                  <span className="p2-lbl">نام شخص حقیقی/حقوقی:</span>
                  <span className="p2-val">{buyer.name || '-'}</span>
                </div>
                <div className="p2-info-item">
                  <span className="p2-lbl">شماره تماس:</span>
                  <span className="p2-val">{toFa(buyer.phone) || '-'}</span>
                </div>
              </div>
              <div className="p2-buyer-row">
                <div className="p2-info-item">
                  <span className="p2-lbl">نشانی کامل:</span>
                  <span className="p2-val">{buyer.address || '-'}</span>
                </div>
                <div className="p2-info-item">
                  <span className="p2-lbl">شهر / استان:</span>
                  <span className="p2-val">{buyerLocation || '-'}</span>
                </div>
              </div>
              {hasBuyerIds && (
                <div className="p2-buyer-row p2-buyer-row-3">
                  <div className="p2-info-item">
                    <span className="p2-lbl">شماره ملی/ثبت:</span>
                    <span className="p2-val">{toFa(buyer.national) || '-'}</span>
                  </div>
                  <div className="p2-info-item">
                    <span className="p2-lbl">شماره اقتصادی:</span>
                    <span className="p2-val">{toFa(buyer.eco) || '-'}</span>
                  </div>
                  <div className="p2-info-item">
                    <span className="p2-lbl">کد پستی:</span>
                    <span className="p2-val">{toFa(buyer.postal) || '-'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ============ جدول اقلام ============ */}
          <table className="p2-table" ref={tableRef}>
            <thead>
              <tr>
                <th style={{ width: `${widths[0]}%` }}>ردیف</th>
                <th style={{ width: `${widths[1]}%` }}>شرح کالا یا خدمات</th>
                <th style={{ width: `${widths[2]}%` }}>مبلغ واحد (ریال)</th>
                <th style={{ width: `${widths[3]}%` }}>تعداد</th>
                <th style={{ width: `${widths[4]}%` }}>مبلغ کل (ریال)</th>
                {hasDiscount && <th style={{ width: `${widths[5]}%` }}>تخفیف (ریال)</th>}
                <th style={{ width: `${widths[hasDiscount ? 6 : 5]}%` }}>
                  مالیات بر ارزش افزوده (ریال)
                </th>
                <th style={{ width: `${widths[hasDiscount ? 7 : 6]}%` }}>جمع کل (ریال)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td>{toFa(i + 1)}</td>
                  <td>
                    <strong>{it.name}</strong>
                    {it.description ? <div className="p2-sub">{toFa(it.description)}</div> : null}
                  </td>
                  <td>{money(it.price)}</td>
                  <td>
                    {toFa(it.qty)} {it.unit || ''}
                  </td>
                  <td>{money(it.gross)}</td>
                  {hasDiscount && <td>{money(it.disc)}</td>}
                  <td>{money(it.tax)}</td>
                  <td>{money(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ============ حساب بانکی و جمع‌ها ============ */}
          <div className="p2-bottom">
            <div className="p2-pay-box">
              <div className="p2-pay-title">اطلاعات حساب بانکی و شرایط پرداخت</div>
              {seller.account_number && (
                <div className="p2-pay-row">
                  <strong>شماره حساب:</strong> {toFa(seller.account_number)}
                </div>
              )}
              {seller.bank_name && (
                <div className="p2-pay-row">
                  <strong>بانک عامل:</strong> {seller.bank_name}
                  {seller.name ? ` (به نام ${seller.holder_name})` : ''}
                </div>
              )}
              {seller.card_number && (
                <div className="p2-pay-row">
                  <strong>شماره کارت:</strong> {toFa(seller.card_number)}
                </div>
              )}
              {seller.iban && (
                <>
                  <div className="p2-pay-row">
                    <strong>شماره شبا:</strong>
                  </div>
                  <div className="p2-iban">{seller.iban}</div>
                </>
              )}
              {!hasBank && <div className="p2-pay-row">-</div>}
              {factor.notes && (
                <div className="p2-pay-row" style={{ marginTop: 6 }}>
                  <strong>توضیحات:</strong> {factor.notes}
                </div>
              )}
            </div>

            <div>
              <table className="p2-totals">
                <tbody>
                  <tr>
                    <td className="p2-lbl-td">مبلغ کل:</td>
                    <td className="p2-val-td">{money(sum.gross)}</td>
                  </tr>
                  {hasDiscount && (
                    <tr>
                      <td className="p2-lbl-td">جمع تخفیف:</td>
                      <td className="p2-val-td">{money(sum.disc)}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="p2-lbl-td">مالیات بر ارزش افزوده:</td>
                    <td className="p2-val-td">{money(sum.tax)}</td>
                  </tr>
                  <tr className="p2-grand">
                    <td className="p2-lbl-td">مبلغ قابل پرداخت (ریال):</td>
                    <td className="p2-val-td">{money(sum.total)}</td>
                  </tr>
                  {isPrepaid && (
                    <>
                      <tr>
                        <td className="p2-lbl-td">پیش پرداخت:</td>
                        <td className="p2-val-td">{money(prepaid)}</td>
                      </tr>
                      <tr>
                        <td className="p2-lbl-td">مانده:</td>
                        <td className="p2-val-td">{money(remaining)}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ============ مهر و امضا ============ */}
        <div className="p2-sigs" ref={sigRef}>
              <div className="p2-sig-box">
            <span className="p2-sig-title">امضاء خریدار</span>
          </div>
          <div className="p2-sig-box">
            <span className="p2-sig-title">مهر و امضاء فروشنده</span>
            <div className="p2-sig-media">
              {showStamp && stampUrl && (
                <img src={stampUrl} alt="مهر" className="p2-stamp" />
              )}
              {showSign && signUrl && (
                <img src={signUrl} alt="امضا" className="p2-signature" />
              )}
         
            </div>
          </div>
        

        </div>
      </div>
    </div>
  );
}